import { useDarkMode } from '@glu/theming';
import { useClickOutside } from '@glu/utilities-react';
import locale from '@glu/locale';
import { useRef, useEffect, useMemo, useCallback, useReducer, isValidElement } from 'react';
import { DropdownProps, PanelOption } from './Dropdown';
import { DropdownTypes, initialState, dropdownReducer } from './DropdownContext';
import { filterOptionsByString, internalFormat, Stub } from './utils/formatOptions';
import { getPreSelected, getPreSelectedFormData } from './utils/getPreSelected';

const useDropdownState = (props: DropdownProps) => {
  const [state, dispatch] = useReducer(dropdownReducer, initialState);
  const dropdownProps = useMemo(
    () => ({
      ariaLabel: locale.get('select'),
      btnClearEnabled: false,
      btnDoneEnabled: true,
      btnSelectAllEnabled: false,
      className: '',
      dataQa: '',
      disabled: false,
      emptyOptionsMessage: undefined,
      errors: [],
      filterBy: 'contains',
      filterEnabled: true,
      filterDebounce: undefined,
      fitParentWidth: false,
      forceOpen: false,
      format: internalFormat,
      htmlId: '',
      isLoading: false,
      labelText: locale.get('select'),
      matchBy: ['name'],
      minSelectedForTooltip: 1,
      multiselect: false,
      filterFunction: filterOptionsByString,
      name: '',
      onBlur: Stub,
      onClear: Stub,
      onChange: Stub,
      onClose: Stub,
      onDone: Stub,
      onFirstOpen: Stub,
      onFilter: Stub,
      onOpen: Stub,
      onSelectAll: Stub,
      onNextPage: Stub,
      placeholder: locale.get('select'),
      rowsPerPage: 50,
      screenReaderLabel: false,
      showGroupInSelection: false,
      showBadges: false,
      showGroups: false,
      showSelected: 5,
      showTooltip: false,
      value: undefined,
      options: [],
      preSelectFirstOption: undefined,
      preSelectedIds: undefined,
      preSelectedNames: undefined,
      ...props,
    }),
    [props]
  );
  const {
    ariaLabel: ariaLabelTextState,
    errors,
    forceOpen,
    htmlId,
    labelText,
    multiselect,
    name,
    onBlur,
    onChange,
    onClose,
    onDone,
    onFirstOpen,
    onOpen,
    options,
    preSelectFirstOption,
    preSelectedIds,
    preSelectedNames,
    value: selectedOptions,
  } = dropdownProps;

  const dark = useDarkMode();
  const [error] = errors || [];

  const { open, selection, wasOpened } = state;
  useEffect(() => {
    dispatch({ type: DropdownTypes.SET_OPEN, payload: forceOpen });
  }, [forceOpen]);

  useEffect(() => {
    dispatch({
      type: DropdownTypes.SET_SELECTION,
      payload: getPreSelected({
        options,
        preSelectFirstOption,
        preSelectedIds,
        preSelectedNames,
        selectedOptions,
      }),
    });
  }, []);

  useEffect(() => {
    if (selectedOptions) {
      const [parsedData, isMutated] = getPreSelectedFormData(selectedOptions, options);
      dispatch({
        type: DropdownTypes.SET_SELECTION,
        payload: parsedData,
      });

      if (isMutated && onChange) {
        // update form context with full data object
        onChange(name, parsedData, true);
      }
    }
  }, [options, selectedOptions]);
  const ariaLabel = useMemo(() => {
    if (isValidElement(labelText)) {
      return ariaLabelTextState;
    }
    return labelText as string;
  }, []);
  const openDropdown = useCallback(
    (value) => {
      dispatch({ type: DropdownTypes.SET_DONE, payload: false });
      if (onFirstOpen && !wasOpened && value) {
        dispatch({ type: DropdownTypes.SET_WAS_OPENED, payload: true });
        onFirstOpen();
      }
      dispatch({ type: DropdownTypes.SET_OPEN, payload: value });
      if (onOpen && value) {
        onOpen();
      }
    },
    [onFirstOpen, onOpen, wasOpened]
  );

  const closeDropdown = useCallback(() => {
    if (open) {
      onBlur();
    }
    onClose();
    dispatch({ type: DropdownTypes.SET_FOCUSED_INDEX, payload: undefined });
    openDropdown(forceOpen);

    dispatch({ type: DropdownTypes.SET_TOOLTIP_OPEN, payload: false });
    dispatch({ type: DropdownTypes.SET_FILTER_FOCUSED, payload: true });
  }, [open, onClose, openDropdown, forceOpen, onBlur]);

  const applySelection = useCallback(
    (value) => {
      closeDropdown();
      if (value !== selection) {
        dispatch({ type: DropdownTypes.SET_SELECTION, payload: value });
        onChange(name, value, true);
      }
      onDone(name, value);
      dispatch({ type: DropdownTypes.SET_DONE, payload: true });
    },
    [closeDropdown, name, onChange, onDone, selection]
  );

  const handleClose = useCallback(() => {
    if (open) {
      closeDropdown();
      applySelection(selection);
    }
  }, [applySelection, closeDropdown, open, selection]);

  /** useClickOutside runs at the document, so esc needs captured earlier to avoid
   ** other modules like modals from catching it and closing */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    },
    [handleClose]
  );

  const uniqueId = useMemo(
    () => htmlId || `dropdown-${(Math.random() * 100000).toFixed()}`,
    [htmlId]
  );

  const divRef = useRef(null);
  useClickOutside(divRef, handleClose);

  useEffect(() => {
    dispatch({
      type: DropdownTypes.SET_DISABLE_PARENT,
      payload: !!divRef.current.closest('fieldset[disabled]'),
    });
  }, [divRef, dispatch]);

  const prevSelectionRef = useRef(selection);
  // multiselects automatically apply changes, so fire onChange event whenever selection changes
  useEffect(() => {
    if (multiselect && JSON.stringify(prevSelectionRef.current) !== JSON.stringify(selection)) {
      prevSelectionRef.current = selection;
      onChange(name, selection as PanelOption[], true);
    }
  }, [selection, multiselect, name, onChange]);

  const setFocusedIndex = useCallback((payload: number) => {
    dispatch({ type: DropdownTypes.SET_FOCUSED_INDEX, payload });
  }, []);

  const setPressedKey = useCallback((payload: string) => {
    dispatch({ type: DropdownTypes.SET_KEY_PRESSED, payload });
  }, []);

  const setSelection = useCallback((payload: PanelOption[]) => {
    dispatch({ type: DropdownTypes.SET_SELECTION, payload });
  }, []);

  const setIsFilterFocused = useCallback((payload: boolean) => {
    dispatch({ type: DropdownTypes.SET_FILTER_FOCUSED, payload });
  }, []);

  return useMemo(
    () => ({
      ariaLabel,
      onBlur,
      handleClose,
      handleKeyDown,
      applySelection,
      openDropdown,
      closeDropdown,
      uniqueId,
      error,
      state,
      dark,
      props: dropdownProps,
      dispatch,
      divRef,
      setFocusedIndex,
      setIsFilterFocused,
      setSelection,
      setPressedKey,
    }),
    [
      ariaLabel,
      applySelection,
      closeDropdown,
      dark,
      dropdownProps,
      error,
      handleClose,
      handleKeyDown,
      onBlur,
      openDropdown,
      setFocusedIndex,
      setIsFilterFocused,
      setPressedKey,
      setSelection,
      state,
      uniqueId,
    ]
  );
};

export default useDropdownState;
